import { FC } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { DragNDropFile } from "components/DragNDropFile";
import { CurriculumFrameworkImport } from "services/aiRequirementsImport";

const FRAMEWORK_TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/templates/competency-framework-template.xlsx`;

type Props = {
  framework: CurriculumFrameworkImport | null;
  error: string | null;
  loading: boolean;
  onFileUploaded: (file: File) => void;
  onNext: () => void;
  onCancel: () => void;
};

function sourceLabel(source: CurriculumFrameworkImport["source"]): string {
  if (source === "cbve_parser") {
    return "Default Template parser";
  }
  if (source === "claude_fallback") {
    return "Claude (fallback)";
  }
  return "Claude";
}

export const FrameworkUploadStep: FC<Props> = ({
  framework,
  error,
  loading,
  onFileUploaded,
  onNext,
  onCancel,
}) => {
  const hasData = Boolean(framework);

  return (
    <StepLayoutContainer
      title="Upload competency framework spreadsheet"
      description="Download the template, keep the same sheet names and columns, then upload your filled .xlsx. The default template parser is preferred; Claude runs only on count mismatches or non-matching layouts."
      onClickNext={hasData && !loading ? onNext : undefined}
      onClickBack={onCancel}
      backButtonText="Cancel"
      nextButtonText="Continue"
      renderActionButtons={
        !hasData || loading ? undefined : (
          <Button variant="contained" color="primary" onClick={onNext} fullWidth>
            Continue
          </Button>
        )
      }
    >
      {error && (
        <Typography color="error" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
          {error}
        </Typography>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Follow the template strictly
        </Typography>
        <Typography variant="body2" component="div">
          Your workbook must keep these sheets and headers exactly:
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>
              <strong>Framework</strong> — expected domain / competency counts
            </li>
            <li>
              <strong>Domains</strong> — Domain, Domain name, Domain description
            </li>
            <li>
              <strong>Competencies</strong> — competency rows linked to a Domain
            </li>
            <li>
              <strong>Milestones</strong> — evaluation levels, with optional
              EPA Reference and Competency Reference overrides
            </li>
            <li>
              <strong>EPAs</strong> (optional) — EPAs with competency and
              domain links
            </li>
            <li>
              <strong>Palette</strong> (optional) — domain Header hex colors
            </li>
            <li>
              <strong>Entrustment supervision</strong> (optional) — only rows
              explicitly referencing Competencies or EPAs replace their
              milestone metrics
            </li>
          </Box>
          Do not rename sheets or required columns. Rows starting with{" "}
          <code>#</code> are treated as comments and ignored.
        </Typography>
        <Button
          sx={{ mt: 1.5 }}
          variant="outlined"
          size="small"
          startIcon={<FileDownload />}
          href={FRAMEWORK_TEMPLATE_URL}
          download="competency-framework-template.xlsx"
        >
          Download framework template
        </Button>
      </Alert>

      <Box mt={2}>
        <DragNDropFile
          maxFileSizeMB={10}
          allowedExtensions={["xlsx", "xls"]}
          onFileUploaded={onFileUploaded}
        />
      </Box>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" mt={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Converting spreadsheet…</Typography>
        </Stack>
      )}

      {framework && !loading && (
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            {framework.framework_title || "Framework preview"}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Source: {sourceLabel(framework.source)}
            {framework.import_blocked
              ? " · import blocked until counts match"
              : ""}
          </Typography>
          {framework.expected_counts && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Framework expects {framework.expected_counts.domains} domains /{" "}
              {framework.expected_counts.competencies} competencies
            </Typography>
          )}
          <Stack spacing={0.5} mt={1}>
            {framework.domains.length > 0 && (
              <Typography variant="body2">
                Domains: {framework.domains.length}
              </Typography>
            )}
            {framework.competencies.length > 0 && (
              <Typography variant="body2">
                Competencies: {framework.competencies.length}
              </Typography>
            )}
            {framework.milestones.length > 0 && (
              <Typography variant="body2">
                Milestones (eval metrics): {framework.milestones.length}
              </Typography>
            )}
            {framework.epas.length > 0 && (
              <Typography variant="body2">
                EPAs: {framework.epas.length}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </StepLayoutContainer>
  );
};
